import { RETENTION_PERIOD_RE } from '@gk/contracts'

/** What `api.retention_overview(p_org)` answers (supabase/migrations/20261115100000_retention.sql, its latest version in 20261116100000_retention_fixes.sql; pgTAP 44 pins it):
 *  change them together. The page "Conservation des données" reads it online, for the direction; nothing of it is kept on the device. A period is ISO 8601 (the database's
 *  `intervalstyle = iso_8601`), in years, months and days only. */
/** 'employment_ended' (M20): the end of the person's last employment period, for the departed employee's information sheet (20261120100200_staff_file.sql);
 *  'incident_closed' (M16-G): the closure of an incident, for the incident register (20261210100600_incidents.sql: an incident still open is never due) */
export type MeasuredFrom = 'recorded' | 'last_activity' | 'session_closed' | 'asset_end_of_life' | 'employment_ended' | 'incident_closed'
export const MEASURED_FROM: readonly MeasuredFrom[] = ['recorded', 'last_activity', 'session_closed', 'asset_end_of_life', 'employment_ended', 'incident_closed']
export interface RetentionCategory {
  category: string
  label: { fr?: string; en?: string }
  measured_from: MeasuredFrom
  default_period: string
  /** M15-C: the legal floor of the category ('P5Y' for the treatment register), null elsewhere; the trigger t50_retention_floor refuses a shorter period */
  min_period?: string | null
  /** false for a club the defaults never reached (the nightly job then leaves the category alone) */
  policy: boolean
  period: string | null
  basis: string | null
  validated_at: string | null
  revision: number | null
  cutoff: string | null
  /** past the period now, by age alone; the next night removes them unless a live record still needs them */
  due: number | null
  due_by_table: Record<string, number>
  /** what the last run kept past the period because a live record still needed it */
  kept_last_run: number
}
export interface RetentionOverview {
  org_id: string
  as_of: string
  categories: RetentionCategory[]
  /** photos whose last link a run removed: waiting for the files job, and already deleted */
  files: { due: number; deleted: number }
  last_run: { at: string; summary: Record<string, unknown> } | null
  last_failure: { at: string; code: string } | null
}

export interface PeriodParts { y: number; m: number; d: number }
export type PeriodUnit = 'Y' | 'M' | 'D'
export const PERIOD_UNITS: readonly PeriodUnit[] = ['Y', 'M', 'D']
/** the server's bounds (RETENTION_PERIOD_INVALID): 1 day to 30 years, as Postgres compares intervals (a month is 30 days, a year 360) */
const MAX: Record<PeriodUnit, number> = { Y: 30, M: 360, D: 10_800 }

/** 'P5Y', 'P1Y1M', 'P30D', 'P1Y1M15D' -> its parts; null for anything else (a time part, weeks, an empty period) */
export function parsePeriod(iso: string | null | undefined): PeriodParts | null {
  const m = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?$/.exec(iso ?? '')
  if (!m || iso === 'P') return null
  return { y: Number(m[1] ?? 0), m: Number(m[2] ?? 0), d: Number(m[3] ?? 0) }
}

/** The same cutoff: Postgres folds twelve months into a year, never days into months (so 'P60M' is 'P5Y', and 'P12M' is not 'P360D'). */
export function samePeriod(a: string | null | undefined, b: string | null | undefined): boolean {
  const x = parsePeriod(a), y = parsePeriod(b)
  return x !== null && y !== null && x.y * 12 + x.m === y.y * 12 + y.m && x.d === y.d
}

/** How long, as Postgres orders intervals (a month is 30 days, a year twelve months): enough to say whether a new period is SHORTER than the one in force. */
export function periodDays(p: PeriodParts): number {
  return (p.y * 12 + p.m) * 30 + p.d
}

/** What the editor starts from: one number and one unit (months when years and months are mixed). Days mixed with months have no exact single unit: the editor
 *  then starts empty, never with a rounded value. */
export function editorValue(p: PeriodParts | null): { n: string; unit: PeriodUnit } {
  if (!p) return { n: '', unit: 'Y' }
  if (p.d > 0) return p.y === 0 && p.m === 0 ? { n: String(p.d), unit: 'D' } : { n: '', unit: 'D' }
  if (p.m > 0) return { n: String(p.y * 12 + p.m), unit: 'M' }
  return { n: String(p.y), unit: 'Y' }
}

/** One number and one unit -> the period the command sends, or null when it is not a whole number within the server's bounds (the server decides again). */
export function toPeriod(n: string, unit: PeriodUnit): string | null {
  if (!/^\d{1,5}$/.test(n.trim())) return null
  const v = Number(n.trim())
  if (v < 1 || v > MAX[unit]) return null
  const iso = `P${v}${unit}`
  return RETENTION_PERIOD_RE.test(iso) ? iso : null
}

/** The parts of a period that are not zero, largest first, for the screen (each is a plural key: `retention.unit.Y`, `.M`, `.D`). */
export function periodPieces(p: PeriodParts): Array<{ unit: PeriodUnit; count: number }> {
  return ([['Y', p.y], ['M', p.m], ['D', p.d]] as const).filter(([, c]) => c > 0).map(([unit, count]) => ({ unit, count }))
}

/** The last failure is news only when no run has succeeded since. */
export function failureSinceLastRun(o: Pick<RetentionOverview, 'last_run' | 'last_failure'>): boolean {
  if (!o.last_failure) return false
  return !o.last_run || Date.parse(o.last_failure.at) > Date.parse(o.last_run.at)
}
